// Employee API client — cookie-authenticated, never reads/writes any token.
// All requests include credentials so the HttpOnly `ordly_employee_session`
// cookie flows. No localStorage. No Authorization header.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function buildUrl(path) {
    if (path.startsWith("http")) return path;
    return `${BACKEND_URL}${path}`;
}

async function eFetch(path, { method = "GET", body, isForm = false } = {}) {
    const headers = {};
    if (!isForm && body !== undefined) headers["Content-Type"] = "application/json";
    let res;
    try {
        res = await fetch(buildUrl(path), {
            method,
            credentials: "include",
            headers,
            body: isForm ? body : body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        // Network failure — surface as ok:false so callers can branch
        // without an unhandled rejection bubbling into the dev overlay.
        return { ok: false, status: 0, data: null };
    }
    if (res.status === 204) return { ok: true, status: 204, data: null };
    let data = null;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    return { ok: res.ok, status: res.status, data };
}

export const employeeApi = {
    me: () => eFetch("/api/e/me"),
    redeem: (token) => eFetch("/api/e/redeem", { method: "POST", body: { token } }),
    logout: () => eFetch("/api/e/logout", { method: "POST" }),

    listDocuments: () => eFetch("/api/e/documents"),
    getDocument: (id) => eFetch(`/api/e/documents/${id}`),
    patchDocument: (id, fields) =>
        eFetch(`/api/e/documents/${id}`, { method: "PATCH", body: fields }),
    retryDocument: (id) =>
        eFetch(`/api/e/documents/${id}/retry`, { method: "POST", body: {} }),
    removeDocument: (id, reason) =>
        eFetch(`/api/e/documents/${id}/request-removal`, {
            method: "POST",
            body: { reason: reason || null },
        }),
    uploadDocument: (file, { source = "upload", note = "", cameraQrCandidate, qrAssistFile } = {}) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("source", source);
        if (note) fd.append("employee_note", note);
        if (cameraQrCandidate) fd.append("camera_qr_candidate", cameraQrCandidate);
        // Optional sidecar — backend (`/api/e/documents`) already accepts
        // `qr_assist_file` and reuses the owner-side QR-assist pipeline.
        if (qrAssistFile) fd.append("qr_assist_file", qrAssistFile);
        return eFetch("/api/e/documents", { method: "POST", body: fd, isForm: true });
    },
    monthSummary: (monthKey) => eFetch(`/api/e/months/${monthKey}/summary`),
};

/**
 * Poll the employee document endpoint until processing converges to a
 * terminal state, mirroring the owner-side `pollDocumentUntilReady` but
 * scoped to /api/e/* only. NEVER calls owner endpoints.
 *
 * Terminal when any of:
 *   - optimization_status in {done, failed}
 *   - company_status in {ready, needs_context, needs_new_photo,
 *                        possible_duplicate, failed}
 *
 * On timeout, throws an Error with `code = "POLL_TIMEOUT"` and the most
 * recent successful document attached as `.lastDoc`.
 */
export async function pollEmployeeDocument(documentId, { intervalMs = 1200, timeoutMs = 60000 } = {}) {
    const start = Date.now();
    const TERMINAL_COMPANY = new Set([
        "ready", "needs_context", "needs_new_photo", "possible_duplicate", "failed",
    ]);
    let lastDoc = null;
    while (Date.now() - start < timeoutMs) {
        const res = await employeeApi.getDocument(documentId);
        if (res.ok && res.data?.document) {
            const doc = res.data.document;
            lastDoc = doc;
            const cs = doc.company_status;
            const opt = doc.optimization_status;
            if (TERMINAL_COMPANY.has(cs) || opt === "done" || opt === "failed") {
                return doc;
            }
        }
        // Transient network/4xx — keep polling until the hard timeout.
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    const err = new Error("Processing timed out");
    err.code = "POLL_TIMEOUT";
    err.lastDoc = lastDoc;
    throw err;
}
